/** @typedef {import('@/types/api').PosRedeemablePackage} PosRedeemablePackage */
/** @typedef {import('@/types/api').Sale} Sale */

/**
 * @param {PosRedeemablePackage | null | undefined} pkg
 */
export function isFlexibleComboPackage(pkg) {
  return pkg?.package_type === 'flexible_combo' && Boolean(pkg?.flexible_combo)
}

/**
 * @param {Sale | null | undefined} sale
 * @param {number} customerPackageId
 */
export function countPendingFlexComboPicks(sale, customerPackageId) {
  if (!sale?.lines) return 0

  return sale.lines
    .filter((line) => (
      line.customer_package_id === customerPackageId
      && line.line_type === 'service'
    ))
    .reduce((sum, line) => sum + (Number(line.quantity) || 1), 0)
}

/**
 * @param {PosRedeemablePackage | null | undefined} pkg
 * @param {Sale | null | undefined} sale
 */
export function flexibleComboPicksAvailable(pkg, sale) {
  if (!isFlexibleComboPackage(pkg)) return 0

  if (pkg.flexible_combo.picks_available_on_ticket !== undefined) {
    return Math.max(0, Number(pkg.flexible_combo.picks_available_on_ticket))
  }

  const remaining = Number(pkg.flexible_combo.picks_remaining)
  const pending = countPendingFlexComboPicks(sale, pkg.customer_package_id)

  return Math.max(0, remaining - pending)
}

/**
 * @param {PosRedeemablePackage | null | undefined} pkg
 * @param {Sale | null | undefined} sale
 */
export function maxFlexibleComboSelections(pkg, sale) {
  return flexibleComboPicksAvailable(pkg, sale)
}

/**
 * @param {number[]} selectedIds
 * @param {number} serviceId
 * @param {number} max
 */
export function toggleFlexibleComboSelection(selectedIds, serviceId, max) {
  if (selectedIds.includes(serviceId)) {
    return selectedIds.filter((id) => id !== serviceId)
  }

  if (selectedIds.length >= max) {
    return selectedIds
  }

  return [...selectedIds, serviceId]
}

/**
 * @param {PosRedeemablePackage | null | undefined} pkg
 * @param {number[]} selectedIds
 * @param {Sale | null | undefined} sale
 */
export function flexibleComboSelectionSummary(pkg, selectedIds, sale) {
  const max = maxFlexibleComboSelections(pkg, sale)
  const selected = selectedIds.length

  if (!isFlexibleComboPackage(pkg)) {
    return { selected, max, label: '' }
  }

  const picksRemaining = pkg.flexible_combo.picks_remaining
  const picksInitial = pkg.flexible_combo.picks_initial

  return {
    selected,
    max,
    label: `Pick ${selected} of ${max} · ${picksRemaining} of ${picksInitial} pick(s) on package`,
  }
}

/**
 * @param {PosRedeemablePackage | null | undefined} pkg
 * @param {string} query
 */
export function filterFlexibleComboMenu(pkg, query) {
  if (!isFlexibleComboPackage(pkg)) return []

  const needle = query.trim().toLowerCase()
  const menu = pkg.flexible_combo.menu

  if (!needle) return menu

  return menu.filter((item) => item.service_name.toLowerCase().includes(needle))
}
